/** Angular */
import { Injectable } from '@angular/core';

/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Sixense */
import { BydModel, BydHttpParams } from '@sixense/byd-core-service';
/** Models */
import { DsLinearWork } from '../models/ds-linear-work';

/** Interfaces */
import { DsLinearWorkFormOrderInterface } from '../interfaces/ds-linear-work-form-order.interface';
import { DsLinearWorkInterface } from '../interfaces/ds-linear-work.interface';
import { MinMaxInterface } from '../../../utils/interfaces/min-max.interface';

@Injectable({
  providedIn: 'root',
})
export class DsLinearWorksService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get all project's linear works.
   *
   * @param projectId Project ID
   * @param elementsPerPage Elements per page
   * @param pageIndex Page index
   * @returns Observable type `<BydModel<DsLinearLane[]>>`
   */
  public getWorks(projectId: number, elementsPerPage = 10, pageIndex = 0): Observable<BydModel<DsLinearWork>> {
    this.path = `/v1/projects/${projectId}/works`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    return this.getModels<DsLinearWork>({ headers });
  }

  /**
   * Get works hierarchy (main works with their annex works as children).
   *
   * @param projectId Project ID
   * @param elementsPerPage Elements per page
   * @param pageIndex Page index
   * @returns Observable<BydModel<DsLinearWork>>
   */
  public getWorksHierarchy(projectId: number, elementsPerPage = 10, pageIndex = 0): Observable<BydModel<DsLinearWork>> {
    this.path = `/v1/projects/${projectId}/works/hierarchy`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    return this.getModels<DsLinearWork>({ headers });
  }

  /**
   * Get selected linear work.
   *
   * @param projectId Selected project id
   * @param workId Selected linear work id
   * @param fields Custom columns to retrieve from database ('name', 'start_pk', 'end_pk', 'attachment_pk', 'type', 'parent_id', 'plan_id', 'previousWork', 'nextWork', null)
   * @returns Observable<DsLinearWork>
   */
  public getLinearWork(projectId: number, workId: number, fields: string[] = []): Observable<DsLinearWork> {
    this.path = `/v1/projects/${projectId}/works/${workId}`;
    const params = new BydHttpParams().set('fields', fields.join());
    return this.getModel<DsLinearWork>({ params });
  }

  /**
   * Add a new linear work to the selected project.
   *
   * @param projectId Selected project id
   * @param newWork Linear work to create (basic infos)
   * @param laneIds Lane ids to link to linear work
   * @param organisationForms Organisation form ids and their order to link to the linear work
   * @param projectForms Project form ids and their order to link to the linear work
   * @returns Observable<number>
   */
  public addLinearWork(
    projectId: number,
    newWork: DsLinearWork,
    laneIds: number[],
    organisationForms: DsLinearWorkFormOrderInterface[],
    projectForms: DsLinearWorkFormOrderInterface[]
  ): Observable<number> {
    const body = {
      laneIds,
      organisationForms,
      projectForms,
      work: newWork,
    };
    this.path = `/v1/projects/${projectId}/works`;
    return this.post<number>({ body });
  }

  /**
   * Add a new linear work to the selected project.
   *
   * @param projectId Selected project id
   * @param laneIds Lane ids to link to linear work
   * @param organisationForms Form ids to link to linear work
   * @param work the linear work
   * @param workId the linear work id
   * @param projectForms Project form ids to link to linear work
   * @returns Observable<number>
   */
  public editLinearWork(
    projectId: number,
    work: DsLinearWork,
    workId: number,
    laneIds: number[],
    organisationForms: DsLinearWorkFormOrderInterface[],
    projectForms: DsLinearWorkFormOrderInterface[]
  ): Observable<number> {
    const body = {
      laneIds,
      organisationForms,
      projectForms,
      work,
    };
    this.path = `/v1/projects/${projectId}/works/${workId}`;
    return this.put<number>({ body });
  }

  /**
   * Get possible KP range for a new linear work.
   *
   * @param projectId Selected project id
   * @returns Observable<MinMaxInterface>
   */
  public getLinearWorkKpRange(projectId: number) {
    this.path = `/v1/projects/${projectId}/works/maxmin`;
    return this.get<MinMaxInterface>();
  }

  /**
   * Delete the selected linear work.
   *
   * @param projectId Selected project id
   * @param workId Work id to delete
   * @param position linear work position
   * @returns Observable<boolean>
   */
  public deleteLinearWork(
    projectId: number,
    workId: number,
    position: '' | 'next' | 'previous' = ''
  ): Observable<boolean> {
    this.path = `/v1/projects/${projectId}/works/${workId}`;
    let params: BydHttpParams;
    if (position) {
      params = new BydHttpParams().set('position', position);
    }
    return this.delete<boolean>({ params });
  }

  /**
   * Create DsLinearWork instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsLinearWork` instance.
   */
  public createNewInstance(jsonObject: DsLinearWorkInterface): DsLinearWork {
    return new DsLinearWork(jsonObject);
  }
}
